import { mapActions } from 'vuex';
import createGuestAccessStore from '@speechki-frontend/core/auth/createGuestAccessStore';

import { checkEditorAvailable } from '../../../editor/api/crm';
import { notifyTokenExpired } from '../../services/tokenExpired';

export default {
    name: 'AuthProvider',

    data() {
        return {
            ready: false
        };
    },

    async created() {
        if (this.$isClientApp && sessionStorage.getItem('guest_access')) {
            createGuestAccessStore({
                token: sessionStorage.getItem('token'),
                refreshToken: sessionStorage.getItem('refresh_token'),
                comebackUrl: sessionStorage.getItem('comeback_url')
            });

            this.$store.subscribe(mutation => {
                if (mutation.type === 'REFRESH_TOKEN_ERROR') {
                    // throttled per book (10 min) to avoid reload loops with Platform
                    notifyTokenExpired();

                    this.$router
                        .push({
                            name: 'error',
                            params: { text: `Access token expired or corrupted` }
                        })
                        .catch(e => {});
                }
            });

            await this.checkEditorAvailability();
        }

        const token = localStorage.getItem('token');

        if (token) {
            await this.checkTokenExpiry(token);
            await this.fetchUser();

            this.$store.subscribe(mutation => {
                if (this.$store.getters.isGuestAccess) return;

                if (mutation.type === 'CLEAR_USER_DATA') {
                    this.$router.push('/login').catch(() => {});
                }
            });
        }

        this.ready = true;
    },

    methods: {
        ...mapActions(['fetchLanguages', 'fetchUser', 'refreshToken']),

        async checkTokenExpiry(token) {
            let expiry = Number(localStorage.getItem('expiry'));
            let diff = expiry - Date.now();

            if (diff > 0 && diff < 8640000) {
                await this.refreshToken(token);
            }
        },

        postToParent(event, detail) {
            if (window.parent && window.parent !== window) {
                window.parent.postMessage({ event, data: { detail } }, '*');
            }
        },

        async showError(text) {
            try {
                await this.$router.push({ name: 'error', params: { text } });
            } catch {
                // navigation duplicated/aborted: the error page is already shown
            }
        },

        /**
         * Fail closed: the editor is rendered only when availability is
         * confirmed. In every other case the error page replaces it.
         */
        async checkEditorAvailability() {
            try {
                const availability = await checkEditorAvailable();

                if (availability?.is_available) {
                    this.ready = true;
                    return;
                }

                const text = `You don't have an access to the order on this stage`;
                this.postToParent('speechki-access-denied', text);
                await this.showError(text);
            } catch (e) {
                const status = e?.status ?? e?.response?.status;

                if (status === 401) {
                    const text = `You don't have an access to the order on this stage`;
                    this.postToParent('speechki-access-denied', text);
                    await this.showError(text);
                } else if (status === 403) {
                    // posted at most once per book per 10 minutes (loop guard)
                    notifyTokenExpired();
                    await this.showError(`Access token expired`);
                } else {
                    const text = 'Technical error occurred, please reload the page later';
                    this.postToParent('speechki-error', text);
                    await this.showError(text);
                }
            }

            // error page is routed; render it instead of the editor
            this.ready = true;
        }
    },

    render() {
        if (this.ready) {
            return this.$slots.default({});
        }

        return null;
    }
};
